import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTraceForBrief } from "../api";
import type { FailureMode, FailurePoint, Granularity } from "../types";
import {
  AGENT_BRIEF_MAX_EXAMPLES,
  buildAgentBrief,
  type AgentBriefExampleInput,
} from "../utils/agentBrief";

export type AgentBriefCopyStatus = "idle" | "loading" | "copied" | "error";

export interface CopyAgentBriefArgs {
  mode: FailureMode;
  granularity: Granularity;
  isLive: boolean;
  /** The mode's in-window example points (already filtered to this mode). */
  examplePoints: FailurePoint[];
}

interface UseAgentBriefCopyResult {
  status: AgentBriefCopyStatus;
  copy: (args: CopyAgentBriefArgs) => Promise<void>;
}

// How long the terminal status lingers on the button before resetting to idle.
const COPIED_RESET_MS = 2500;
const ERROR_RESET_MS = 4000;

/**
 * Most-recent-first, then capped: the newest failures are the most useful for an
 * agent trying to reproduce the mode. The server already returns points newest
 * first, but re-sorting here means the brief never silently depends on that.
 */
function pickExamplePoints(points: FailurePoint[]): FailurePoint[] {
  return [...points]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, AGENT_BRIEF_MAX_EXAMPLES);
}

/**
 * Fetch each example's full content (a failed per-trace fetch degrades to an
 * explanation-only example) and assemble the formatted brief JSON.
 */
async function buildBriefJson(args: CopyAgentBriefArgs): Promise<string> {
  const points = pickExamplePoints(args.examplePoints);
  const traces = await Promise.all(
    points.map((p) => fetchTraceForBrief(p.trace_id).catch(() => null)),
  );
  const examples: AgentBriefExampleInput[] = points.map((point, i) => ({
    point,
    trace: traces[i],
  }));
  const brief = buildAgentBrief({
    mode: args.mode,
    granularity: args.granularity,
    isLive: args.isLive,
    examples,
  });
  return JSON.stringify(brief, null, 2);
}

/**
 * Copy the brief to the clipboard, keeping the write inside the click's user
 * activation. Safari/WebKit revokes clipboard permission across an `await` for
 * network I/O, so we hand the (fetch + build) promise to `ClipboardItem`: the
 * `clipboard.write` call is issued synchronously within the gesture and the
 * async work resolves under it. Firefox has no promise-valued `ClipboardItem`,
 * so there we fall back to `writeText` (its activation rules are lenient enough
 * that an awaited write still succeeds).
 */
async function writeBriefToClipboard(
  briefJson: () => Promise<string>,
): Promise<void> {
  if (typeof ClipboardItem === "function" && navigator.clipboard?.write) {
    const blob = briefJson().then(
      (text) => new Blob([text], { type: "text/plain" }),
    );
    await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
  } else {
    await navigator.clipboard.writeText(await briefJson());
  }
}

/**
 * Assemble a coding-agent brief for a failure mode and copy it to the clipboard
 * (issue #1324). Fetches full content for up to `AGENT_BRIEF_MAX_EXAMPLES` of the
 * mode's example traces in parallel — a per-trace fetch that fails (deleted /
 * ZDR / cross-domain) degrades to its explanation-only example rather than
 * failing the whole copy. `status` drives the button label; the deferred reset
 * is cancelled when the panel itself unmounts (e.g. navigating away from the
 * page — not merely closing, which keeps it mounted) so it never sets state
 * after teardown.
 */
export function useAgentBriefCopy(): UseAgentBriefCopyResult {
  const [status, setStatus] = useState<AgentBriefCopyStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(async (args: CopyAgentBriefArgs) => {
    clearTimeout(timer.current);
    setStatus("loading");
    try {
      await writeBriefToClipboard(() => buildBriefJson(args));
      setStatus("copied");
      timer.current = setTimeout(() => setStatus("idle"), COPIED_RESET_MS);
    } catch (err) {
      console.error("Failed to copy coding-agent brief:", err);
      setStatus("error");
      timer.current = setTimeout(() => setStatus("idle"), ERROR_RESET_MS);
    }
  }, []);

  return { status, copy };
}
